import { useCallback } from 'react';
import { Task } from '../types';
import { users } from '../data/sampleData';

export const useTaskManagement = (
  tasks: Task[],
  onTasksUpdate: (tasks: Task[]) => void,
  onTimelineEvent: (event: any) => void
) => {
  const handleTaskUpdate = useCallback((taskId: string, updates: Partial<Task>) => {
    const taskIndex = tasks.findIndex(t => t.id === taskId);
    if (taskIndex === -1) return;

    const oldTask = tasks[taskIndex];
    const updatedTask = { 
      ...oldTask,
      ...updates,
      metadata: {
        ...oldTask.metadata,
        ...updates.metadata,
        ...(updates.status === 'completed' && !updates.metadata?.completedAt ? {
          completedAt: new Date().toISOString()
        } : {})
      }
    };
    const updatedTasks = [...tasks];
    updatedTasks[taskIndex] = updatedTask;

    // Add timeline event for status change
    if (updates.status && updates.status !== oldTask.status) {
      const user = users.find(u => u.id === oldTask.assignedTo);
      const userName = user ? user.name : 'Unknown user';
      
      onTimelineEvent({
        date: new Date().toISOString(),
        description: `Task "${oldTask.title}" marked as ${updates.status} by ${userName}`,
        type: updates.status === 'completed' ? 'form-submission' : 'note',
        addedBy: oldTask.assignedTo,
        taskId: oldTask.id,
      });
    }

    onTasksUpdate(updatedTasks);
  }, [tasks, onTasksUpdate, onTimelineEvent]);

  const handleTaskCreate = useCallback((taskData: Partial<Task>) => {
    const newTask: Task = {
      id: `t${Date.now()}`,
      title: taskData.title || '',
      description: taskData.description || '',
      assignedTo: taskData.assignedTo || '',
      dueDate: taskData.dueDate || new Date().toISOString(),
      status: taskData.status as Task['status'] || 'pending',
      priority: taskData.priority as Task['priority'] || 'medium',
      stageId: taskData.stageId || '',
    };
    
    onTasksUpdate([...tasks, newTask]);
    
    onTimelineEvent({
      date: new Date().toISOString(),
      description: `New task created: "${newTask.title}"`,
      type: 'note',
      addedBy: '1',
      taskId: newTask.id,
    });
  }, [tasks, onTasksUpdate, onTimelineEvent]);

  return {
    handleTaskUpdate,
    handleTaskCreate,
  };
};